// Discount code checkbox on the product page (snippets/product-discount-code.liquid).
// Ticking it applies the code to the cart, copies it to the clipboard and shows
// the unit price after the discount; unticking removes the code from the cart.
// While ticked, the main price block shows the discounted price, and the
// add-to-cart prices (<atc-price>, product-info.js) take the discount off their
// total; `discount-code:change` tells them to re-render.
if (!customElements.get('product-discount-code')) {
  customElements.define(
    'product-discount-code',
    class ProductDiscountCode extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.product-discount-code__input');
        this.finalPriceEl = this.querySelector('[data-final-price]');
        this.codeGroup = this.querySelector('.product-discount-code__code-group');
        this.copiedEl = this.querySelector('.product-discount-code__copied');
        this.cartQueue = Promise.resolve();
        this.priceContainer = (this.closest('product-info') || document).querySelector(
          `[id="price-${this.dataset.sectionId}"]`,
        );
        // product-info (variant change) and the fulfillment selector (pickup)
        // rewrite the price block, wiping the discounted price; re-apply it after.
        this.priceObserver = new MutationObserver(() => this.renderMainPrice());

        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.input.addEventListener('change', () => this.onChange(), { signal });
        // The fulfillment selector switches the unit price to the pickup price.
        document.addEventListener('fulfillment:pricing-change', () => this.renderPrice(), { signal });

        this.unsubscribeVariantChange = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          ({ data }) => {
            if (data?.sectionId !== this.dataset.sectionId || !data.variant) return;
            this.dataset.variantId = data.variant.id;
            this.dataset.price = data.variant.price;
            this.dataset.compareAtPrice = data.variant.compare_at_price || 0;
            this.renderPrice();
          },
        );

        this.renderPrice();
        this.restoreState();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.unsubscribeVariantChange?.();
        this.priceObserver.disconnect();
        clearTimeout(this.copiedTimer);
        // Removed (e.g. re-rendered in the theme editor): drop the discounted prices.
        this.removeAttribute('data-applied');
        this.renderMainPrice();
        this.priceObserver.disconnect();
        document.dispatchEvent(new CustomEvent('discount-code:change'));
      }

      get code() {
        return this.dataset.code;
      }

      // Pickup price while pickup pricing is active for the current variant (set
      // by the fulfillment selector, same check as <atc-price>), else the variant price.
      get unitPrice() {
        const { sectionId, variantId } = this.dataset;
        const selector = document.querySelector(
          `fulfillment-selector[data-section-id="${sectionId}"][data-pickup-price]`,
        );
        if (selector && selector.dataset.pickupVariantId === variantId) {
          return Number(selector.dataset.pickupPrice);
        }
        return Number(this.dataset.price);
      }

      // Takes the discount off an amount in cents: a unit price, or a whole
      // add-to-cart total, since a fixed amount comes off once per order.
      applyDiscount(amount) {
        const value = parseFloat(this.dataset.discountValue) || 0;
        if (this.dataset.discountType === 'percentage') {
          return Math.round((amount * (100 - Math.min(value, 100))) / 100);
        }
        // Fixed amounts are set in the shop currency; checkout converts them to
        // the visitor's currency at the market rate.
        const rate = Number(window.Shopify?.currency?.rate) || 1;
        return Math.max(0, amount - Math.round(value * 100 * rate));
      }

      formatMoney(amount) {
        return FoxTheme.Currency.formatMoney(amount, FoxTheme.settings.moneyFormat);
      }

      renderPrice() {
        this.finalPriceEl.innerHTML = this.formatMoney(this.applyDiscount(this.unitPrice));
        this.renderMainPrice();
      }

      // While ticked, hides the price block's own price and shows a copy of it
      // with the discounted unit price as the sale price, struck through against
      // the higher of the compare-at and variant prices.
      renderMainPrice() {
        const container = this.priceContainer;
        if (!container) return;
        this.priceObserver.disconnect();

        container.querySelectorAll('[data-discount-code-price]').forEach((price) => price.remove());
        const original = container.querySelector('.f-price');
        const applied = this.hasAttribute('data-applied') && !!original;
        container.classList.toggle('has-discount-code', applied);
        if (applied) original.after(this.buildMainPrice(original));

        this.priceObserver.observe(container, { childList: true, subtree: true, characterData: true });
      }

      buildMainPrice(original) {
        const finalPrice = this.applyDiscount(this.unitPrice);
        const strikePrice = Math.max(Number(this.dataset.compareAtPrice) || 0, Number(this.dataset.price));
        const savings = strikePrice - finalPrice;

        const price = original.cloneNode(true);
        price.setAttribute('data-discount-code-price', '');
        price.classList.add('f-price--on-sale');
        price.classList.remove('f-price--no-compare');
        price.querySelectorAll('.f-price__regular .f-price-item--regular').forEach((item) => (item.innerHTML = ''));

        const sale = price.querySelector('.f-price__sale');
        const saleItem = sale.querySelector('.f-price-item--sale');
        if (saleItem) saleItem.innerHTML = this.formatMoney(finalPrice);
        let strikeItem = sale.querySelector('.f-price-item--regular');
        if (!strikeItem) {
          strikeItem = document.createElement('span');
          strikeItem.className = 'f-price-item f-price-item--regular';
          sale.append(strikeItem);
        }
        strikeItem.innerHTML = `<s>${this.formatMoney(strikePrice)}</s>`;

        // Same markup as snippets/price.liquid, keeping its "X% OFF," prefix if shown.
        let savingsEl = price.querySelector('.f-price__savings');
        const showPercentOff = /%/.test(savingsEl?.textContent || '');
        if (savings > 0) {
          if (!savingsEl) {
            savingsEl = document.createElement('div');
            savingsEl.className = 'f-price__savings';
            sale.after(savingsEl);
          }
          const percentOff = Math.round((savings * 100) / strikePrice);
          savingsEl.innerHTML = `${showPercentOff ? `<span>${percentOff}% OFF,</span>` : ''}<span>SAVE ${this.formatMoney(savings)}</span>`;
        } else {
          savingsEl?.remove();
        }
        return price;
      }

      setApplied(applied) {
        this.toggleAttribute('data-applied', applied);
        this.renderMainPrice();
        document.dispatchEvent(new CustomEvent('discount-code:change'));
      }

      onChange() {
        const applied = this.input.checked;
        this.setApplied(applied);
        if (applied) this.copyCode();

        // Serialized so quick toggles reach the cart in order; each run syncs to
        // the checkbox state at that moment.
        this.cartQueue = this.cartQueue
          .then(() => this.syncCart())
          .catch((error) => console.error('[ProductDiscountCode] failed to update cart discount', error));
      }

      async copyCode() {
        try {
          await navigator.clipboard.writeText(this.code);
        } catch (error) {
          return;
        }
        clearTimeout(this.copiedTimer);
        this.placeCopied();
        this.copiedEl.classList.add('is-visible');
        this.copiedTimer = setTimeout(() => this.copiedEl.classList.remove('is-visible'), 2000);
      }

      // Beside the code box when the row has room for it, else inside the box in
      // place of the code (e.g. on mobile, where the code ends the row).
      placeCopied() {
        this.codeGroup.dataset.copiedMode = 'beside';
        const contentRight = this.getBoundingClientRect().right - parseFloat(getComputedStyle(this).paddingRight);
        if (this.copiedEl.getBoundingClientRect().right > contentRight) {
          this.codeGroup.dataset.copiedMode = 'inside';
        }
      }

      isOwnCode(code) {
        return String(code).toLowerCase() === this.code.toLowerCase();
      }

      cartCodes(cart) {
        return (cart?.discount_codes || []).map(({ code }) => code);
      }

      // Code saved by a /discount/<code> visit, applied when checkout starts.
      get cookieCode() {
        const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
        if (!match) return null;
        try {
          return decodeURIComponent(match[1]);
        } catch (error) {
          return match[1];
        }
      }

      async fetchCart() {
        const response = await fetch(`${FoxTheme.routes.cart_url}.js`, FoxTheme.utils.fetchConfig('json', 'GET'));
        if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
        return response.json();
      }

      // Ticks the checkbox when the code is already on the cart, e.g. after a reload.
      async restoreState() {
        try {
          const cart = await this.fetchCart();
          const codes = [...this.cartCodes(cart), this.cookieCode].filter(Boolean);
          if (!codes.some((code) => this.isOwnCode(code))) return;
          this.input.checked = true;
          this.setApplied(true);
        } catch (error) {
          // Leave the checkbox unticked.
        }
      }

      async syncCart() {
        const apply = this.input.checked;
        const cart = await this.fetchCart();
        // `discount` replaces every code on the cart, so keep the ones added elsewhere.
        const codes = this.cartCodes(cart).filter((code) => !this.isOwnCode(code));
        if (apply) codes.push(this.code);

        const response = await fetch(`${FoxTheme.routes.cart_update_url}.js`, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({ discount: codes.join(',') }),
        });
        if (!response.ok) throw new Error(`cart/update.js responded ${response.status}`);
        const updatedCart = await response.json();

        if (apply && !this.cartCodes(updatedCart).some((code) => this.isOwnCode(code))) {
          // Fallback when the cart did not keep the code (e.g. no cart yet): the
          // discount_code cookie set here is picked up at checkout.
          const rootUrl = FoxTheme.routes.root_url.replace(/\/$/, '');
          const redirect = encodeURIComponent(`${FoxTheme.routes.cart_url}.js`);
          await fetch(`${rootUrl}/discount/${encodeURIComponent(this.code)}?redirect=${redirect}`);
        } else if (!apply && this.cookieCode && this.isOwnCode(this.cookieCode)) {
          document.cookie = 'discount_code=; Max-Age=0; path=/';
        }

        // Re-render the cart drawer so its totals reflect the change.
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
      }
    },
  );
}
